import { Modal, Button, Form, Select, DatePicker, Space, Typography, Divider, Input, Row, Col, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useState, useEffect, useMemo } from 'react';
import locale from 'antd/es/date-picker/locale/pt_BR';
import 'dayjs/locale/pt-br';
import { inspectionTypeService } from '@/services/inspectionType.service';
import { inspectionReasonService } from '@/services/inspectionReason.service';
import { providerCompanyService } from '@/services/providerCompany.service';
import { inspectorUserService } from '@/services/inspectorUser.service';
import { bankUserService } from '@/services/bankUser.service';
import { providerUserService } from '@/services/providerUser.service';
import { cityService } from '@/services/city.service';
import { inspectionService } from '@/services/inspection.service';
import type { ExportFilterOptions } from '@/services/types/inspection.types';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { INSPECTION_ITEM_STATUS_MAP } from '@/shared/constants/statusMaps';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface ReportsModalProps {
  open: boolean;
  onClose: () => void;
  onExportPanorama: (filters?: ExportFilters) => void;
  isExporting: boolean;
}

export interface ExportFilters {
  statuses?: string[];
  inspectionTypeIds?: string[];
  inspectionReasonIds?: string[];
  providerIds?: string[];
  inspectorIds?: string[];
  reportAuthorIds?: string[];
  states?: string[];
  cities?: string[];
  isScheduled?: boolean;
  startDate?: string;
  endDate?: string;
}

/** Opção do filtro "Inspetor": userId (valor gravado em inspection_assignments.inspector_id). */
export interface InspectorFilterOption {
  userId: string;
  name: string;
  cpf?: string | null;
  /** Prestadoras sob as quais o inspetor tem direcionamento (cascata Prestadora → Inspetor). */
  providerIds: string[];
  /** false quando o cadastro não conhece mais o usuário (desativado). */
  isRegistered: boolean;
}

/** Opção do filtro "Elaborador do Laudo": userId (valor gravado em created_by / actor_user_id). */
export interface ReportAuthorFilterOption {
  userId: string;
  name: string;
  /** Inspetor | Prestadora | Banco | Inativo */
  origem: string;
  /** Quantidade de itens cujo laudo o usuário transmitiu (quando vem do backend). */
  itemCount?: number;
  isRegistered: boolean;
}

/**
 * FALLBACK (quando o endpoint de opções do backend falha): junta usuários de prestadora e
 * do banco numa lista única de possíveis autores de laudo, deduplicada por userId — que é o
 * campo gravado em inspection_reports.created_by e o que o filtro do backend compara.
 * Inspetores não entram: laudo é elaborado só pela Prestadora ou pelo Banco. Não inclui
 * usuários já desativados no cadastro.
 */
export function buildReportAuthors(
  providerUsers: any[] = [],
  bankUsers: any[] = [],
): { userId: string; name: string; origem: string }[] {
  const porUserId = new Map<string, { userId: string; name: string; origem: string }>();

  const adicionar = (lista: any[], origem: string) => {
    (lista || []).forEach((u) => {
      const userId = u?.userId;
      if (!userId || porUserId.has(userId)) return;
      porUserId.set(userId, { userId, name: u.name || u.email || 'Sem nome', origem });
    });
  };

  // Mesma prioridade do login quando um userId tem mais de um perfil: banco, depois prestadora
  adicionar(bankUsers, 'Banco');
  adicionar(providerUsers, 'Prestadora');

  return Array.from(porUserId.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/** FALLBACK: inspetores do cadastro → opções, com a prestadora do vínculo cadastral. */
export function buildInspectorOptionsFromRegistry(inspectors: any[] = []): InspectorFilterOption[] {
  const porUserId = new Map<string, InspectorFilterOption>();

  (inspectors || []).forEach((inspector) => {
    const userId = inspector?.userId;
    if (!userId || porUserId.has(userId)) return;
    porUserId.set(userId, {
      userId,
      name: inspector.name || inspector.email || 'Sem nome',
      cpf: inspector.cpf,
      providerIds: inspector.providerId ? [inspector.providerId] : [],
      isRegistered: true,
    });
  });

  return Array.from(porUserId.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/**
 * Cascata Prestadora → Inspetor: com prestadoras selecionadas, mostra os inspetores que
 * atuam sob elas. Os inspetores JÁ SELECIONADOS nunca somem da lista (nem da seleção):
 * o backend combina os filtros com E, então uma seleção "fora da cascata" só restringe
 * mais — nunca traz linha errada.
 */
export function filterInspectorOptions(
  options: InspectorFilterOption[],
  selectedProviderIds: string[] = [],
  selectedInspectorIds: string[] = [],
): InspectorFilterOption[] {
  if (!selectedProviderIds || selectedProviderIds.length === 0) {
    return options;
  }

  return options.filter(
    (option) =>
      selectedInspectorIds.includes(option.userId) ||
      option.providerIds.some((providerId) => selectedProviderIds.includes(providerId)),
  );
}

/**
 * Inspetores selecionados que NÃO têm nenhuma vistoria nas prestadoras selecionadas (pela
 * prestadora atual de cada imóvel). Como o backend combina os filtros com E, a exportação
 * não trará nenhuma linha deles: o modal avisa, em vez de apagar a seleção do usuário.
 */
export function findInspectorsOutsideProviders(
  options: InspectorFilterOption[],
  selectedProviderIds: string[] = [],
  selectedInspectorIds: string[] = [],
): InspectorFilterOption[] {
  if (!selectedProviderIds.length || !selectedInspectorIds.length) {
    return [];
  }

  return options.filter(
    (option) =>
      selectedInspectorIds.includes(option.userId) &&
      !option.providerIds.some((providerId) => selectedProviderIds.includes(providerId)),
  );
}

/**
 * Ao recarregar as listas (reabrir o modal), mantém as opções dos valores JÁ selecionados
 * que não vieram na lista nova (ex.: usuário inativo quando a consulta caiu no cadastro).
 * Sem isso a seleção continua valendo no formulário, mas a tag mostraria o código em vez
 * do nome.
 */
export function mergeKeepingSelected<T extends { userId: string }>(
  next: T[],
  previous: T[],
  selectedIds: string[] = [],
): T[] {
  const nextIds = new Set(next.map((option) => option.userId));
  const kept = previous.filter((option) => selectedIds.includes(option.userId) && !nextIds.has(option.userId));
  return kept.length > 0 ? [...next, ...kept] : next;
}

export function mapFilterOptions(options: ExportFilterOptions): {
  inspectors: InspectorFilterOption[];
  reportAuthors: ReportAuthorFilterOption[];
} {
  return {
    inspectors: (options?.inspectors || []).map((i) => ({
      userId: i.userId,
      name: i.name,
      cpf: i.cpf,
      providerIds: i.providerIds || [],
      isRegistered: i.isRegistered,
    })),
    // Laudo é elaborado só pela Prestadora ou pelo Banco: inspetor nunca entra na lista
    reportAuthors: (options?.reportAuthors || []).filter((a) => a.origin !== 'Inspetor').map((a) => ({
      userId: a.userId,
      name: a.name,
      origem: a.origin,
      itemCount: a.itemCount,
      isRegistered: a.isRegistered,
    })),
  };
}

export function ReportsModal({ open, onClose, onExportPanorama, isExporting }: ReportsModalProps) {
  const [form] = Form.useForm();
  const [inspectionTypes, setInspectionTypes] = useState<any[]>([]);
  const [inspectionReasons, setInspectionReasons] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [inspectorOptions, setInspectorOptions] = useState<InspectorFilterOption[]>([]);
  const [reportAuthors, setReportAuthors] = useState<ReportAuthorFilterOption[]>([]);
  // 'dados': opções derivadas das vistorias (backend); 'cadastro': plano B pelas listas de usuários
  const [optionsSource, setOptionsSource] = useState<'dados' | 'cadastro'>('dados');
  const [cities, setCities] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Carregar opções de filtros
  useEffect(() => {
    if (open) {
      loadFilterOptions();
    }
  }, [open]);

  const loadFilterOptions = async () => {
    setLoadingOptions(true);
    // Cada lista é aplicada (ou zerada, se falhar) individualmente: uma falha não derruba as
    // outras nem deixa a lista da abertura anterior. Enquanto a resposta não chega, as listas
    // atuais continuam na tela, então valores já selecionados não viram códigos sem nome.
    const falhas: string[] = [];
    let usouCadastro = false;

    try {
      const [typesRes, reasonsRes, providersRes, optionsRes] = await Promise.allSettled([
        inspectionTypeService.getAllInspectionTypes(),
        inspectionReasonService.getAll(),
        providerCompanyService.getAllProviders(),
        // Inspetores e elaboradores vêm dos DADOS (direcionamentos e transmissões de
        // laudo), inclusive usuários desativados: só aparece quem pode casar com alguma linha.
        inspectionService.getExportFilterOptions(),
      ]);

      if (typesRes.status === 'fulfilled') {
        setInspectionTypes(typesRes.value || []);
      } else {
        setInspectionTypes([]);
        falhas.push('Tipo de Inspeção');
      }

      if (reasonsRes.status === 'fulfilled') {
        setInspectionReasons(reasonsRes.value || []);
      } else {
        setInspectionReasons([]);
        falhas.push('Finalidade');
      }

      if (providersRes.status === 'fulfilled') {
        setProviders(providersRes.value || []);
      } else {
        setProviders([]);
        falhas.push('Prestadora');
      }

      const selectedInspectorIds: string[] = form.getFieldValue('inspectorIds') || [];
      const selectedAuthorIds: string[] = form.getFieldValue('reportAuthorIds') || [];

      if (optionsRes.status === 'fulfilled') {
        const mapped = mapFilterOptions(optionsRes.value);
        setInspectorOptions((previous) => mergeKeepingSelected(mapped.inspectors, previous, selectedInspectorIds));
        setReportAuthors((previous) => mergeKeepingSelected(mapped.reportAuthors, previous, selectedAuthorIds));
        setOptionsSource('dados');
      } else {
        // Plano B: listas do cadastro (não incluem usuários desativados)
        usouCadastro = true;
        const [inspectorsRes, bankUsersRes, providerUsersRes] = await Promise.allSettled([
          inspectorUserService.getAllInspectorUsers(),
          bankUserService.getAllBankUsers(),
          providerUserService.getAllProviderUsers(),
        ]);
        const inspectors = inspectorsRes.status === 'fulfilled' ? inspectorsRes.value || [] : [];
        const bankUsers = bankUsersRes.status === 'fulfilled' ? bankUsersRes.value || [] : [];
        const providerUsers = providerUsersRes.status === 'fulfilled' ? providerUsersRes.value || [] : [];

        if (inspectorsRes.status === 'rejected') falhas.push('Inspetor');

        const fontesSemCarga = [
          providerUsersRes.status === 'rejected' ? 'usuários de prestadora' : null,
          bankUsersRes.status === 'rejected' ? 'usuários do banco' : null,
        ].filter((fonte): fonte is string => fonte !== null);
        if (fontesSemCarga.length === 2) {
          falhas.push('Elaborador do Laudo');
        } else if (fontesSemCarga.length > 0) {
          falhas.push(`Elaborador do Laudo (faltam ${fontesSemCarga.join(', ')})`);
        }

        const registryInspectors = buildInspectorOptionsFromRegistry(inspectors);
        const registryAuthors: ReportAuthorFilterOption[] = buildReportAuthors(providerUsers, bankUsers)
          .map((a) => ({ ...a, isRegistered: true }));
        setInspectorOptions((previous) => mergeKeepingSelected(registryInspectors, previous, selectedInspectorIds));
        setReportAuthors((previous) => mergeKeepingSelected(registryAuthors, previous, selectedAuthorIds));
        setOptionsSource('cadastro');
      }
    } finally {
      setLoadingOptions(false);
    }

    if (usouCadastro) {
      message.warning(
        'As opções de Inspetor e Elaborador do Laudo foram carregadas do cadastro porque a consulta pelas vistorias falhou: ' +
          'usuários desativados não aparecem e a cascata Prestadora → Inspetor usa o vínculo cadastral.',
        10,
      );
    }

    if (falhas.length > 0) {
      message.error(
        `Não foi possível carregar as opções de: ${falhas.join('; ')}. ` +
          'Esses filtros estão indisponíveis ou incompletos nesta exportação — feche e abra o relatório para tentar de novo.',
        10,
      );
    }
  };

  // Observar mudanças nos campos do formulário
  const selectedProviders = Form.useWatch('providerIds', form);
  const selectedInspectors = Form.useWatch('inspectorIds', form);
  const selectedStates = Form.useWatch('states', form);

  // Filtro cascata: Prestadora → Inspetor (sem apagar seleções)
  const filteredInspectors = useMemo(
    () => filterInspectorOptions(inspectorOptions, selectedProviders || [], selectedInspectors || []),
    [inspectorOptions, selectedProviders, selectedInspectors],
  );

  // Aviso quando a combinação Prestadora + Inspetor não tem vistoria nenhuma. Só com as
  // opções derivadas das vistorias: no plano B o vínculo cadastral pode não refletir os dados.
  const inspectorsOutsideProviders = useMemo(
    () =>
      optionsSource === 'dados'
        ? findInspectorsOutsideProviders(inspectorOptions, selectedProviders || [], selectedInspectors || [])
        : [],
    [optionsSource, inspectorOptions, selectedProviders, selectedInspectors],
  );

  // Filtro cascata: Estados → Municípios
  useEffect(() => {
    if (!selectedStates || selectedStates.length === 0) {
      setCities([]);
      return;
    }

    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const citiesPromises = selectedStates.map((state: string) =>
          cityService.getCitiesByState(state)
        );
        const citiesArrays = await Promise.all(citiesPromises);
        const allCities = citiesArrays.flat();
        const uniqueCities = Array.from(new Set(allCities)).sort();
        setCities(uniqueCities);
      } catch {
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, [selectedStates]);

  // Handler para mudança de Estados
  const handleStatesChange = () => {
    // Limpa o campo de município quando os estados mudam
    form.setFieldValue('city', undefined);
  };

  // Status disponíveis para filtro (mesmos rótulos usados na coluna Status da planilha)
  const statusOptions = Object.entries(InspectionItemStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => {
      const statusInfo = INSPECTION_ITEM_STATUS_MAP[value as InspectionItemStatus];
      return {
        label: statusInfo?.label || key,
        value: value.toString(),
      };
    });

  // Estados brasileiros
  const stateOptions = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ].map(state => ({ label: state, value: state }));

  const handleExport = () => {
    const values = form.getFieldsValue();

    const filters: ExportFilters = {};

    if (values.statuses && values.statuses.length > 0) {
      filters.statuses = values.statuses;
    }
    if (values.inspectionTypeIds && values.inspectionTypeIds.length > 0) {
      filters.inspectionTypeIds = values.inspectionTypeIds;
    }
    if (values.inspectionReasonIds && values.inspectionReasonIds.length > 0) {
      filters.inspectionReasonIds = values.inspectionReasonIds;
    }
    if (values.providerIds && values.providerIds.length > 0) {
      filters.providerIds = values.providerIds;
    }
    if (values.inspectorIds && values.inspectorIds.length > 0) {
      filters.inspectorIds = values.inspectorIds;
    }
    if (values.reportAuthorIds && values.reportAuthorIds.length > 0) {
      filters.reportAuthorIds = values.reportAuthorIds;
    }
    if (values.states && values.states.length > 0) {
      filters.states = values.states;
    }
    if (values.city && values.city.length > 0) {
      filters.cities = values.city;
    }
    // Limpar o select (allowClear) deixa o valor como null — só filtra se true/false
    if (values.isScheduled !== undefined && values.isScheduled !== null) {
      filters.isScheduled = values.isScheduled;
    }
    if (values.dateRange && values.dateRange.length === 2) {
      filters.startDate = values.dateRange[0].startOf('day').toISOString();
      filters.endDate = values.dateRange[1].endOf('day').toISOString();
    }

    onExportPanorama(filters);
  };

  const handleClearFilters = () => {
    form.resetFields();
  };

  const inspectorLabel = (inspector: InspectorFilterOption) =>
    inspector.isRegistered ? inspector.name : `${inspector.name} (inativo)`;

  const reportAuthorLabel = (author: ReportAuthorFilterOption) => {
    const contagem =
      author.itemCount !== undefined && author.itemCount !== null
        ? ` · ${author.itemCount} ${author.itemCount === 1 ? 'laudo' : 'laudos'}`
        : '';
    return `${author.name} (${author.origem})${contagem}`;
  };

  return (
    <Modal
      title="Exportar Relatórios"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Text strong>Utilize os filtros para especificar os dados a serem exportados</Text>
            <Text type="secondary">
              Os filtros se combinam (E): cada linha exportada atende a todos ao mesmo tempo.
              Prestadora e Inspetor consideram o direcionamento atual do imóvel; Elaborador considera
              quem transmitiu alguma versão do laudo — as colunas correspondentes saem na planilha.
            </Text>
          </div>
          <Button type="link" onClick={handleClearFilters}>
            Limpar Filtros
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
        >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Status"
                    name="statuses"
                    tooltip="Filtre por status específicos das inspeções"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todos os status"
                      options={statusOptions}
                      loading={loadingOptions}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Tipo de Inspeção"
                    name="inspectionTypeIds"
                    tooltip="Filtre por um ou mais tipos de inspeção"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todos os tipos"
                      optionFilterProp="label"
                      options={inspectionTypes.map(type => ({
                        label: type.name,
                        value: type.id,
                      }))}
                      loading={loadingOptions}
                      allowClear
                      showSearch
                      maxTagCount="responsive"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Finalidade"
                    name="inspectionReasonIds"
                    tooltip="Filtre por uma ou mais finalidades da inspeção"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todas as finalidades"
                      optionFilterProp="label"
                      options={inspectionReasons.map(reason => ({
                        label: reason.name,
                        value: reason.id,
                      }))}
                      loading={loadingOptions}
                      allowClear
                      showSearch
                      maxTagCount="responsive"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Prestadora"
                    name="providerIds"
                    tooltip="Prestadora do direcionamento ATUAL do imóvel (o mais recente não cancelado nem recusado) — o mesmo que aparece na coluna Prestadora da planilha"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todas as prestadoras"
                      optionFilterProp="label"
                      options={providers.map(provider => ({
                        label: provider.name,
                        value: provider.id,
                      }))}
                      loading={loadingOptions}
                      allowClear
                      showSearch
                      maxTagCount="responsive"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Período de Criação"
                    name="dateRange"
                    tooltip="Período de criação do imóvel (item), do início do dia inicial ao fim do dia final"
                  >
                    <RangePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      placeholder={['Data inicial', 'Data final']}
                      locale={locale}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Inspetor"
                    name="inspectorIds"
                    validateStatus={inspectorsOutsideProviders.length > 0 ? 'warning' : undefined}
                    help={
                      inspectorsOutsideProviders.length > 0
                        ? `Sem vistorias nas prestadoras selecionadas: ${inspectorsOutsideProviders
                            .map((inspector) => inspector.name)
                            .join(', ')}. A exportação não trará linhas desse(s) inspetor(es).`
                        : undefined
                    }
                    tooltip="Inspetor do direcionamento ATUAL do imóvel (o mais recente, não cancelado, que tem inspetor) — o mesmo que aparece na coluna Inspetor da planilha. A lista traz quem tem direcionamento, inclusive inativos"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todos os inspetores"
                      options={filteredInspectors.map(inspector => ({
                        label: inspectorLabel(inspector),
                        value: inspector.userId,
                      }))}
                      loading={loadingOptions}
                      allowClear
                      showSearch
                      maxTagCount="responsive"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Elaborador do Laudo"
                    name="reportAuthorIds"
                    tooltip="Usuário da Prestadora ou do Banco que transmitiu alguma versão do laudo do imóvel (elaboração, correção ou edição pós-aceite). Confira nas colunas 'Elaborador do Laudo' e 'Laudo Retransmitido por' da planilha. Se a mesma pessoa tem mais de um login, selecione todos"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todos os elaboradores"
                      options={reportAuthors.map(author => ({
                        label: reportAuthorLabel(author),
                        value: author.userId,
                      }))}
                      loading={loadingOptions}
                      allowClear
                      showSearch
                      maxTagCount="responsive"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Estados (UF)"
                    name="states"
                    tooltip="Filtre por um ou mais estados do imóvel"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Todos os estados"
                      options={stateOptions}
                      allowClear
                      showSearch
                      maxTagCount="responsive"
                      onChange={handleStatesChange}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Município"
                    name="city"
                    tooltip="Filtre por um ou mais municípios do imóvel"
                  >
                    {cities.length > 0 ? (
                      <Select
                        mode="multiple"
                        placeholder="Selecione os municípios"
                        options={cities.map(city => ({
                          label: city,
                          value: city,
                        }))}
                        loading={loadingCities}
                        allowClear
                        showSearch
                        maxTagCount="responsive"
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    ) : (
                      <Input
                        placeholder="Selecione estados para carregar municípios"
                        disabled
                        allowClear
                      />
                    )}
                  </Form.Item>

                  <Form.Item
                    label="Status de Agendamento"
                    name="isScheduled"
                    tooltip="Filtre por inspeções agendadas ou não agendadas"
                  >
                    <Select
                      placeholder="Todas"
                      allowClear
                      options={[
                        { label: 'Agendadas', value: true },
                        { label: 'Não Agendadas', value: false },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
        </Form>

        <Divider />

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={isExporting}
          >
            {isExporting ? 'Gerando Relatório...' : 'Exportar'}
          </Button>
        </Space>
      </Space>
    </Modal>
  );
}
